import { forwardRef, useEffect, useId, useRef, type RefObject } from "react";
import {
  PHONE_DIAL_OPTIONS,
  combinePhoneParts,
  dialCodeForCountry,
  parsePhoneParts,
} from "../lib/phoneInput";

const fieldClass =
  "rounded-xl border border-white/10 bg-white/5 text-base text-white outline-none transition-colors focus:border-brand-pink/60 lg:text-sm";
const selectClass = `${fieldClass} shrink-0 px-2 py-2.5`;
const inputClass = `${fieldClass} min-w-0 flex-1 px-3.5 py-2.5 placeholder-white/30`;

type PhoneInputProps = {
  label?: string;
  hint?: string;
  value: string;
  onChange: (value: string) => void;
  /** ISO country — updates default dial code when empty or unchanged number. */
  country?: string;
  /** Lock dial code (e.g. SMS verify = Malaysia only). */
  lockDialCode?: string;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  numberRef?: RefObject<HTMLInputElement>;
};

export const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(function PhoneInput(
  {
    label,
    hint,
    value,
    onChange,
    country,
    lockDialCode,
    placeholder = "Mobile number",
    className = "",
    disabled,
    numberRef,
  },
  ref,
) {
  const hintId = useId();
  const defaultDial = lockDialCode ?? dialCodeForCountry(country);
  const parsed = parsePhoneParts(value, defaultDial);
  const dialCode = lockDialCode ?? parsed.dialCode;
  const localNumber = parsed.localNumber;

  const prevCountry = useRef<string | undefined>(country);
  useEffect(() => {
    if (lockDialCode || !country) {
      prevCountry.current = country;
      return;
    }
    if (country === prevCountry.current) return;
    const prior = prevCountry.current;
    prevCountry.current = country;
    if (!prior) return;
    const { localNumber: num } = parsePhoneParts(value, dialCodeForCountry(prior));
    onChange(combinePhoneParts(dialCodeForCountry(country), num));
  }, [country, lockDialCode, value, onChange]);

  function handleDialChange(nextDial: string) {
    onChange(combinePhoneParts(nextDial, localNumber));
  }

  function handleNumberChange(nextLocal: string) {
    onChange(combinePhoneParts(dialCode, nextLocal));
  }

  const options = lockDialCode
    ? PHONE_DIAL_OPTIONS.filter((o) => o.code === lockDialCode)
    : PHONE_DIAL_OPTIONS;

  return (
    <div className={className}>
      {label && (
        <span id={hintId} className="mb-1 block text-xs font-medium text-white/60">
          {label}
        </span>
      )}
      <div className="flex gap-2">
        <select
          aria-label="Country code"
          className={`${selectClass} w-[6.75rem]`}
          value={dialCode}
          disabled={disabled || !!lockDialCode}
          onChange={(e) => handleDialChange(e.target.value)}
        >
          {options.map((o) => (
            <option key={o.code} value={o.code} className="bg-surface text-foreground">
              {o.label}
            </option>
          ))}
        </select>
        <input
          ref={numberRef ?? ref}
          type="tel"
          inputMode="tel"
          autoComplete="tel-national"
          className={inputClass}
          placeholder={placeholder}
          value={localNumber}
          disabled={disabled}
          aria-labelledby={label ? hintId : undefined}
          onChange={(e) => handleNumberChange(e.target.value)}
        />
      </div>
      {hint && <span className="mt-1 block text-[10px] text-white/40">{hint}</span>}
    </div>
  );
});
